import { ErrorResponse } from '@lemon/core/model/common'
import { LlmTestiajoTila } from './llm-kirjausehdotus-testaus'

export type TositteenBusinessIdTyyppi = 'y-tunnus' | 'vat'
export type TositteenTyyppi = 'ostolasku' | 'kuitti'
export type LlmTositeLukuTestitapausLahde = 'lemonaid' | 'ladattu'

/** lemonator = lemonatorin tositteen luku (tosite-ocr.service), lemonaid = webin ja mobiilin lukuputki (tosite-ai-luku.service). */
export const LLM_TOSITE_LUKU_PUTKET = ['lemonator', 'lemonaid'] as const
export type LlmTositeLukuPutki = (typeof LLM_TOSITE_LUKU_PUTKET)[number]

/** testiajo = malli lukee tositteet ja sen vastaus pisteytetään; jalkikasittely = testiajon mallin vastaukset jälkikäsiteltyinä kuten tuotannossa. */
export type LlmTositeLukuAjoLaji = 'testiajo' | 'jalkikasittely'

/** Pisteytettävät kentät. lemonator-putken mallin vastauksessa ei ole tyyppiä. */
export interface TositteenLuetutTiedot {
  paivamaara: string | null
  summa: number | null
  toimittaja: string | null
  businessId: string | null
  businessIdType: TositteenBusinessIdTyyppi | null
  toimittajaAvain: string | null
  tyyppi?: TositteenTyyppi | null
}

export const TOSITTEEN_TIEDOT_KENTAT = ['paivamaara', 'summa', 'toimittaja', 'businessId', 'businessIdType', 'toimittajaAvain', 'tyyppi'] as const
export type TositteenTiedotKentta = (typeof TOSITTEEN_TIEDOT_KENTAT)[number]

/** null = kenttää ei arvioitu tälle testitapaukselle (ei syötettyä odotusarvoa). */
export type TositteenTiedotOsumat = Record<TositteenTiedotKentta, boolean | null>

/**
 * Käsin syötetty ground truth. Ainoa asia, jota vasten ajot pisteytetään.
 * toimittaja null = "ei luettavissa", jolloin mallilta odotetaan null. businessId syötetään kuten tositteeseen on painettu.
 * paivamaara ja summa ovat valinnaisia: null/puuttuva = kenttää ei arvioida.
 * toimittajaAvain puuttuva = odotettu rekisterin toimittaja johdetaan tunnuksesta ja nimestä; null = testeri on
 * päättänyt, ettei myyjä ole rekisterissä; merkkijono = testerin valitsema rekisterin toimittaja.
 */
export interface TositteenMetatiedot {
  toimittaja: string | null
  businessId: string | null
  paivamaara?: string | null
  summa?: number | null
  toimittajaAvain?: string | null
  /** Puuttuva = tyyppiä ei arvioida. Arvioidaan vain lemonaid-putkessa. */
  tyyppi?: TositteenTyyppi
}

/** Lemonaid-tositteen tämänhetkiset tiedot lisäyshetkellä. Vain viitteeksi, ei koskaan odotusarvona. */
export interface TositteenViitetiedot {
  paivamaara: string | null
  summa: number | null
  toimittajaAvain: string | null
  toimittajaNimi: string | null
  toimittajaTunniste: string | null
}

export interface LlmTositeLukuTiedosto {
  polku: string
  mediaTyyppi: string
  nimi: string
}

export const LLM_TOSITE_LUKU_MALLIT = ['gemini-3.5-flash', 'gemini-3.5-flash-lite', 'gemini-3.5-pro', 'claude-haiku-5-5', 'claude-sonnet-5-5'] as const
export type LlmTositeLukuMalli = (typeof LLM_TOSITE_LUKU_MALLIT)[number]
export const LLM_TOSITE_LUKU_OLETUSMALLI: LlmTositeLukuMalli = 'gemini-3.5-flash'
/** Putken tuotannossa käyttämä malli: tosite-ocr.service TOSITE_OCR_OLETUS_MALLI ja tosite-ai-luku.service TOSITE_AI_OLETUS_MALLI. */
export const LLM_TOSITE_LUKU_PUTKEN_OLETUSMALLI: Record<LlmTositeLukuPutki, LlmTositeLukuMalli> = { lemonator: 'gemini-3.5-flash', lemonaid: 'gemini-3.5-flash-lite' }
export const LLM_TOSITE_LUKU_PUTKEN_NIMI: Record<LlmTositeLukuPutki, string> = { lemonator: 'Lemonatorin luku', lemonaid: 'Webin ja mobiilin luku' }

export const LLM_TOSITE_LUKU_LADATTAVAT_MEDIATYYPIT = ['image/jpeg', 'image/png', 'image/webp', 'application/pdf'] as const
/** Callable-payloadin raja on ~10 MB, joten base64-sisältö rajataan 8 MB:iin. */
export const LLM_TOSITE_LUKU_TIEDOSTON_MAX_BASE64_TAVUT = 8 * 1024 * 1024

/** Sama muoto kuin tuotanto lähettää mallille (tosite-ocr.service ToimittajaRekisteritieto). */
export interface LlmTositeLukuToimittajaRekisteritieto {
  avain: string
  numero: number
  nimi: string
  tunnisteet: string[]
  ibanit: string[]
  /** Vain lemonaid-putken mallin jälkeiseen täsmäykseen, ei lähetetä mallille. Puuttuu ennen putkea tallennetuista snapshoteista. */
  ytunnus?: string | null
}

export interface LlmTositeLukuPromptRow {
  versio: number
  prompt: string
  /** ISO 8601 -merkkijono. */
  paivitetty: string
  paivittajaUid: string | null
  putki: LlmTositeLukuPutki
}

/** Puuttuva putki = lemonator. */
export interface LlmTositeLukuHaePromptUusin {
  putki?: LlmTositeLukuPutki
}
export interface LlmTositeLukuHaePromptUusinVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  rivi: LlmTositeLukuPromptRow | null
}

export interface LlmTositeLukuHaePromptHistoria {
  putki?: LlmTositeLukuPutki
}
export interface LlmTositeLukuHaePromptHistoriaVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  rivit: LlmTositeLukuPromptRow[]
}

export interface LlmTositeLukuTallennaPrompt {
  prompt: string
  putki?: LlmTositeLukuPutki
}
export interface LlmTositeLukuTallennaPromptVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  versio?: number
}

export interface LlmTositeLukuPalautaPromptVersio {
  versio: number
}
export interface LlmTositeLukuPalautaPromptVersioVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  versio?: number
}

export interface LlmTositeLukuTestisetti {
  avain: string
  nimi: string
  kuvaus: string | null
  /** ISO 8601 -merkkijono. */
  luotu: string
  luojaUid: string
  testitapausLkm: number
}

/** Yksi testitapaus = yksi lemonaid-tosite (lemonatorin KirjattavaKuitti) tai yksi ladattu tiedosto + käsin syötetyt metatiedot. Kuuluu yhteen settiin. */
export interface LlmTositeLukuTestitapaus {
  avain: string
  testisettiAvain: string
  lahde: LlmTositeLukuTestitapausLahde
  /** Näyttönimi: lemonaid-tapaukselle asiakas + tosite, ladatulle tiedostonimi. */
  nimi: string
  asiakasAvain: string | null
  asiakasId: string | null
  asiakasNimi: string | null
  tositeAvain: string | null
  metatiedot: TositteenMetatiedot
  tositteenTiedot: TositteenViitetiedot | null
  tiedostot: LlmTositeLukuTiedosto[] | null
  toimittajaRekisteri: LlmTositeLukuToimittajaRekisteritieto[]
  /** ISO 8601 -merkkijono. */
  toimittajaRekisteriPaivitetty: string
  /** ISO 8601 -merkkijono. */
  lisatty: string
  /** YYYY-MM-DD. lemonaid-putken "tänään": mallille kerrottava päivä ja jälkikäsittelyn nykyhetki. */
  viitepaiva: string
}

export interface LlmTositeLukuHaeSetit {}
export interface LlmTositeLukuHaeSetitVastaus extends ErrorResponse<'unknown-error'> {
  setit: LlmTositeLukuTestisetti[]
}

export interface LlmTositeLukuLuoSetti {
  nimi: string
  kuvaus?: string
}
export interface LlmTositeLukuLuoSettiVastaus extends ErrorResponse<'missing-data' | 'nimi-varattu' | 'unknown-error'> {
  avain?: string
}

export interface LlmTositeLukuPoistaSetti {
  avain: string
}
export interface LlmTositeLukuPoistaSettiVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

/** Yhden lemonaid-tositteen jäsenyys yhdessä setissä. Sama tosite voi olla useassa setissä. */
export interface LlmTositeLukuSettijasenyys {
  tositeAvain: string
  testitapausAvain: string
  settiAvain: string
  settiNimi: string
  metatiedot: TositteenMetatiedot
}

export interface LlmTositeLukuHaeAsiakkaanSettijasenyydet {
  asiakasAvain: string
}
export interface LlmTositeLukuHaeAsiakkaanSettijasenyydetVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  jasenyydet: LlmTositeLukuSettijasenyys[]
}

/** Lemonaid-tositteen nykyiset tiedot viiteblokkiin ennen tallennusta sekä asiakkaan toimittajarekisteri odotetun toimittajan valintaan. */
export interface LlmTositeLukuHaeTositteenViitetiedot {
  asiakasAvain: string
  tositeAvain: string
}
export interface LlmTositeLukuHaeTositteenViitetiedotVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  tositteenTiedot?: TositteenViitetiedot
  toimittajaRekisteri?: LlmTositeLukuToimittajaRekisteritieto[]
}

/** Lisää yhden testitapauksen: joko lemonaid-tosite tai ladattu tiedosto, ei molempia. */
export interface LlmTositeLukuLisaaTestitapaus {
  settiAvain: string
  metatiedot: TositteenMetatiedot
  tosite?: {
    asiakasAvain: string
    tositeAvain: string
  }
  tiedosto?: {
    nimi: string
    mediaTyyppi: string
    base64Sisalto: string
  }
}
export interface LlmTositeLukuLisaaTestitapausVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'duplikaatti' | 'tiedosto-liian-suuri' | 'unknown-error'> {
  avain?: string
}

export interface LlmTositeLukuHaeSettiTestitapaukset {
  settiAvain: string
}
export interface LlmTositeLukuHaeSettiTestitapauksetVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  rivit: LlmTositeLukuTestitapaus[]
}

export interface LlmTositeLukuLadattuTiedostoSisalto {
  nimi: string
  mediaTyyppi: string
  base64Sisalto: string
}

/** Ladatun testitapauksen tiedostot esikatseluun; lemonaid-tapaukselle 'not-found'. */
export interface LlmTositeLukuHaeLadatutTiedostot {
  testitapausAvain: string
}
export interface LlmTositeLukuHaeLadatutTiedostotVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  tiedostot: LlmTositeLukuLadattuTiedostoSisalto[]
}

export interface LlmTositeLukuPaivitaTestitapausMetatiedot {
  avain: string
  metatiedot: TositteenMetatiedot
  /** YYYY-MM-DD. Puuttuva = viitepäivä säilyy ennallaan. */
  viitepaiva?: string
}
export interface LlmTositeLukuPaivitaTestitapausMetatiedotVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {}

export interface LlmTositeLukuPoistaTestitapaus {
  avain: string
}
export interface LlmTositeLukuPoistaTestitapausVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {}

/** Lataa toimittajarekisterin uudelleen ja kirjoittaa snapshotin setin kaikille (tai yhdelle) lemonaid-testitapaukselle. Metatietoihin ei kosketa. */
export interface LlmTositeLukuPaivitaToimittajaRekisterit {
  settiAvain: string
  testitapausAvain?: string
}
export interface LlmTositeLukuPaivitaToimittajaRekisteritVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  paivitetty: number
  /** Testitapaukset, joiden käsin valittua toimittajaa ei enää löydy uudesta rekisteristä. */
  valittuToimittajaPuuttuu: string[]
}

export interface LlmTositeLukuAloitaTestiAjo {
  settiAvain: string
  malli: LlmTositeLukuMalli
  /** Jos annettu, ajo käyttää tätä promptversiota; muuten putken uusinta. */
  promptVersio?: number
  /** Puuttuva = lemonator. */
  putki?: LlmTositeLukuPutki
}
export interface LlmTositeLukuAloitaTestiAjoVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'invalid-malli' | 'invalid-putki' | 'no-prompt' | 'no-testitapauksia' | 'unknown-error'> {
  ajoAvain?: string
}

export type TositteenTiedotTarkkuusProsentit = Record<TositteenTiedotKentta, number | null>

export interface LlmTositeLukuAjoRow {
  avain: string
  /** ISO 8601 -merkkijono. */
  aloitettu: string
  paattynyt: string | null
  tila: LlmTestiajoTila
  malli: string
  promptVersio: number
  prompt: string
  testisettiAvain: string | null
  testisettiNimi: string
  kayttajanUid: string
  tositteita: number
  onnistuneita: number | null
  tarkkuusProsentit: TositteenTiedotTarkkuusProsentit
  taydellisetOsumat: number | null
  yhteensaInputTokenit: number | null
  yhteensaOutputTokenit: number | null
  virheViesti: string | null
  putki: LlmTositeLukuPutki
  laji: LlmTositeLukuAjoLaji
  /** Jälkikäsittelyajolla testiajo, jonka mallin vastauksista se laskettiin. */
  lahdeAjoAvain: string | null
}

export interface LlmTositeLukuHaeTestiajot {
  settiAvain?: string
}
export interface LlmTositeLukuHaeTestiajotVastaus extends ErrorResponse<'unknown-error'> {
  ajot: LlmTositeLukuAjoRow[]
}

export interface LlmTositeLukuTulosRow {
  avain: string
  ajoAvain: string
  testitapausAvain: string | null
  nimi: string
  asiakasAvain: string | null
  tositeAvain: string | null
  /** Pisteytyshetkellä metatiedoista ja rekisterisnapshotista johdetut odotusarvot. Arvioimattomat kentät ovat null ja osumat[kentta] on null. */
  odotetutTiedot: TositteenLuetutTiedot
  /** Testiajossa mallin vastaus, jälkikäsittelyajossa lopulliset arvot (null myös, kun kuva on lukukelvoton). */
  luetutTiedot: TositteenLuetutTiedot | null
  osumat: TositteenTiedotOsumat
  virhe: string | null
  /** undefined = ei haettu (haeTulokset ei sisällä sitä bulkkina); haetaan erikseen haeTuloksenRaakaVastaus-operaatiolla vasta kun rivi avataan. */
  raakaVastaus?: string | null
  inputTokenit: number
  outputTokenit: number
  /** ISO 8601 -merkkijono. */
  ajettu: string
}

export interface LlmTositeLukuHaeTulokset {
  ajoAvain: string
}
export interface LlmTositeLukuHaeTuloksetVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  tulokset: LlmTositeLukuTulosRow[]
}

export interface LlmTositeLukuHaeTuloksenRaakaVastaus {
  tulosAvain: string
}
export interface LlmTositeLukuHaeTuloksenRaakaVastausVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  raakaVastaus: string | null
}

/** Jälkikäsittelee valmiin lemonaid-testiajon mallin vastaukset uudeksi ajoksi. */
export interface LlmTositeLukuAjaJalkikasittely {
  ajoAvain: string
}
export interface LlmTositeLukuAjaJalkikasittelyVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'invalid-ajo' | 'unknown-error'> {
  ajoAvain?: string
}

export interface LlmTositeLukuHaeTestiajonEdistyminen {
  ajoAvain: string
}
export interface LlmTositeLukuHaeTestiajonEdistyminenVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  tila?: LlmTestiajoTila
  kasiteltyLkm?: number
}

/** Tuotannon AI-poimintojen tilasto (Postgres llm_tosite_luku_tilasto): Lemonaidin mobiili- ja web-poiminnat verrattuna tallennettuun tositteeseen. */
export const LLM_TOSITE_LUKU_TUOTANTO_KENTAT = ['paivamaara', 'summa', 'valuutta', 'toimittajaAvain'] as const
export type LlmTositeLukuTuotantoKentta = (typeof LLM_TOSITE_LUKU_TUOTANTO_KENTAT)[number]

export interface LlmTositeLukuTuotantoKentanYhteenveto {
  osumia: number
  arvioituja: number
  /** Tallennettuja rivejä, joissa malli ei antanut kentälle arvoa vaikka käyttäjä täytti sen. */
  eiLuettu: number
}

export interface LlmTositeLukuTuotantoYhteenvetoRivi {
  /** Kuukauden ensimmäinen päivä muodossa YYYY-MM-DD. */
  kuukausi: string
  malli: string | null
  promptTunniste: string | null
  tallennusKanava: string | null
  poimintoja: number
  virheita: number
  tallennettuja: number
  kentat: Record<LlmTositeLukuTuotantoKentta, LlmTositeLukuTuotantoKentanYhteenveto>
  inputTokenit: number
  outputTokenit: number
}

export interface LlmTositeLukuHaeTuotantotilasto {
  /** YYYY-MM-DD, molemmat päivät mukaan lukien. */
  alkaen: string
  asti: string
}
export interface LlmTositeLukuHaeTuotantotilastoVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  rivit?: LlmTositeLukuTuotantoYhteenvetoRivi[]
}

export interface LlmTositeLukuTuotantoRivi {
  poimintaAvain: string
  asiakasId: string | null
  asiakasAvain: string | null
  asiakasNimi: string | null
  tositeAvain: string | null
  malli: string | null
  promptTunniste: string | null
  tallennusKanava: string | null
  /** ISO 8601 -merkkijonot. */
  luotu: string | null
  tallennettu: string | null
  ehdotettuPaivamaara: string | null
  tallennettuPaivamaara: string | null
  ehdotettuSumma: number | null
  tallennettuSumma: number | null
  ehdotettuValuutta: string | null
  tallennettuValuutta: string | null
  ehdotettuToimittaja: string | null
  tallennettuToimittaja: string | null
  ehdotettuToimittajaAvain: string | null
  tallennettuToimittajaAvain: string | null
  /** null = ei arvioitavissa (malli ei antanut arvoa). */
  osumat: Record<LlmTositeLukuTuotantoKentta, boolean | null>
}

export interface LlmTositeLukuHaeTuotantoOsumattomat {
  alkaen: string
  asti: string
  /** Puuttuva = mikä tahansa kenttä osumaton. */
  kentta?: LlmTositeLukuTuotantoKentta
  /** 1–500 */
  raja: number
}
export interface LlmTositeLukuHaeTuotantoOsumattomatVastaus extends ErrorResponse<'missing-data' | 'unknown-error'> {
  rivit?: LlmTositeLukuTuotantoRivi[]
}

export interface LlmTositeLukuHaeTuotantoPerustelu {
  poimintaAvain: string
}
export interface LlmTositeLukuHaeTuotantoPerusteluVastaus extends ErrorResponse<'missing-data' | 'not-found' | 'unknown-error'> {
  reasoning?: string | null
  raakaVastaus?: string | null
}

export type LlmTositeLukuOp =
  | 'haePromptUusin'
  | 'haePromptHistoria'
  | 'tallennaPrompt'
  | 'palautaPromptVersio'
  | 'haeSetit'
  | 'luoSetti'
  | 'poistaSetti'
  | 'haeAsiakkaanSettijasenyydet'
  | 'haeTositteenViitetiedot'
  | 'lisaaTestitapaus'
  | 'haeSettiTestitapaukset'
  | 'haeLadatutTiedostot'
  | 'paivitaTestitapausMetatiedot'
  | 'poistaTestitapaus'
  | 'paivitaToimittajaRekisterit'
  | 'aloitaTestiAjo'
  | 'ajaJalkikasittely'
  | 'haeTestiajot'
  | 'haeTulokset'
  | 'haeTuloksenRaakaVastaus'
  | 'haeTestiajonEdistyminen'
  | 'haeTuotantotilasto'
  | 'haeTuotantoOsumattomat'
  | 'haeTuotantoPerustelu'

export interface LlmTositeLukuPyyntoMap {
  haePromptUusin: { req: LlmTositeLukuHaePromptUusin; resp: LlmTositeLukuHaePromptUusinVastaus }
  haePromptHistoria: { req: LlmTositeLukuHaePromptHistoria; resp: LlmTositeLukuHaePromptHistoriaVastaus }
  tallennaPrompt: { req: LlmTositeLukuTallennaPrompt; resp: LlmTositeLukuTallennaPromptVastaus }
  palautaPromptVersio: { req: LlmTositeLukuPalautaPromptVersio; resp: LlmTositeLukuPalautaPromptVersioVastaus }
  haeSetit: { req: LlmTositeLukuHaeSetit; resp: LlmTositeLukuHaeSetitVastaus }
  luoSetti: { req: LlmTositeLukuLuoSetti; resp: LlmTositeLukuLuoSettiVastaus }
  poistaSetti: { req: LlmTositeLukuPoistaSetti; resp: LlmTositeLukuPoistaSettiVastaus }
  haeAsiakkaanSettijasenyydet: { req: LlmTositeLukuHaeAsiakkaanSettijasenyydet; resp: LlmTositeLukuHaeAsiakkaanSettijasenyydetVastaus }
  haeTositteenViitetiedot: { req: LlmTositeLukuHaeTositteenViitetiedot; resp: LlmTositeLukuHaeTositteenViitetiedotVastaus }
  lisaaTestitapaus: { req: LlmTositeLukuLisaaTestitapaus; resp: LlmTositeLukuLisaaTestitapausVastaus }
  haeSettiTestitapaukset: { req: LlmTositeLukuHaeSettiTestitapaukset; resp: LlmTositeLukuHaeSettiTestitapauksetVastaus }
  haeLadatutTiedostot: { req: LlmTositeLukuHaeLadatutTiedostot; resp: LlmTositeLukuHaeLadatutTiedostotVastaus }
  paivitaTestitapausMetatiedot: { req: LlmTositeLukuPaivitaTestitapausMetatiedot; resp: LlmTositeLukuPaivitaTestitapausMetatiedotVastaus }
  poistaTestitapaus: { req: LlmTositeLukuPoistaTestitapaus; resp: LlmTositeLukuPoistaTestitapausVastaus }
  paivitaToimittajaRekisterit: { req: LlmTositeLukuPaivitaToimittajaRekisterit; resp: LlmTositeLukuPaivitaToimittajaRekisteritVastaus }
  aloitaTestiAjo: { req: LlmTositeLukuAloitaTestiAjo; resp: LlmTositeLukuAloitaTestiAjoVastaus }
  ajaJalkikasittely: { req: LlmTositeLukuAjaJalkikasittely; resp: LlmTositeLukuAjaJalkikasittelyVastaus }
  haeTestiajot: { req: LlmTositeLukuHaeTestiajot; resp: LlmTositeLukuHaeTestiajotVastaus }
  haeTulokset: { req: LlmTositeLukuHaeTulokset; resp: LlmTositeLukuHaeTuloksetVastaus }
  haeTuloksenRaakaVastaus: { req: LlmTositeLukuHaeTuloksenRaakaVastaus; resp: LlmTositeLukuHaeTuloksenRaakaVastausVastaus }
  haeTestiajonEdistyminen: { req: LlmTositeLukuHaeTestiajonEdistyminen; resp: LlmTositeLukuHaeTestiajonEdistyminenVastaus }
  haeTuotantotilasto: { req: LlmTositeLukuHaeTuotantotilasto; resp: LlmTositeLukuHaeTuotantotilastoVastaus }
  haeTuotantoOsumattomat: { req: LlmTositeLukuHaeTuotantoOsumattomat; resp: LlmTositeLukuHaeTuotantoOsumattomatVastaus }
  haeTuotantoPerustelu: { req: LlmTositeLukuHaeTuotantoPerustelu; resp: LlmTositeLukuHaeTuotantoPerusteluVastaus }
}

export type LlmTositeLukuPyynto = {
  [K in LlmTositeLukuOp]: { op: K; payload: LlmTositeLukuPyyntoMap[K]['req'] }
}[LlmTositeLukuOp]
